import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';

export const SpotifyCallback: React.FC = () => {
    const { user } = useAuth();
    const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        const handleCallback = async () => {
            const urlParams = new URLSearchParams(window.location.search);
            const code = urlParams.get('code');
            const state = urlParams.get('state');
            const error = urlParams.get('error');

            if (error) {
                setStatus('error');
                setErrorMessage(urlParams.get('error_description') || error);
                return;
            }

            if (!code) {
                setStatus('error');
                setErrorMessage('No authorization code received');
                return;
            }

            try {
                const supabaseUrl = (import.meta as any).env.VITE_SUPABASE_URL;
                const supabaseAnonKey = (import.meta as any).env.VITE_SUPABASE_ANON_KEY;
                const callbackUrl = `${window.location.origin}/spotify-callback`;

                const response = await fetch(
                    `${supabaseUrl}/functions/v1/spotify-auth?action=callback&code=${code}&state=${encodeURIComponent(state || '')}&redirect_uri=${encodeURIComponent(callbackUrl)}`,
                    {
                        headers: {
                            'apikey': supabaseAnonKey,
                        }
                    }
                );

                const data = await response.json();

                if (data.success) {
                    setStatus('success');

                    // Show success toast
                    window.dispatchEvent(new CustomEvent('app:toast', {
                        detail: { message: 'Spotify connected successfully!', type: 'success' }
                    }));

                    // Redirect back to the saved route
                    const returnRoute = sessionStorage.getItem('spotify_return_route') || '#story';
                    sessionStorage.removeItem('spotify_return_route');

                    // Small delay to show success before redirecting
                    setTimeout(() => {
                        window.location.href = window.location.origin + '/' + returnRoute;
                    }, 1000);
                } else {
                    throw new Error(data.error || 'Unknown error');
                }
            } catch (err: any) {
                setStatus('error');
                setErrorMessage(err.message);
            }
        };

        handleCallback();
    }, []);

    return (
        <div className="min-h-screen bg-zinc-900 flex items-center justify-center">
            <div className="text-center space-y-4">
                {status === 'loading' && (
                    <>
                        <div className="w-12 h-12 border-4 border-green-500 border-t-transparent rounded-full animate-spin mx-auto" />
                        <p className="text-white text-lg">Connecting to Spotify...</p>
                    </>
                )}

                {status === 'success' && (
                    <>
                        <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mx-auto">
                            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                        </div>
                        <p className="text-white text-lg">Spotify Connected!</p>
                        <p className="text-zinc-400 text-sm">Redirecting...</p>
                    </>
                )}

                {status === 'error' && (
                    <>
                        <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center mx-auto">
                            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </div>
                        <p className="text-white text-lg">Connection Failed</p>
                        <p className="text-red-400 text-sm">{errorMessage}</p>
                        <button
                            onClick={() => window.location.href = window.location.origin}
                            className="mt-4 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white rounded transition-colors"
                        >
                            Return Home
                        </button>
                    </>
                )}
            </div>
        </div>
    );
};
